import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, BlockingOverlay, Button, Text } from '@datanomik/core-ui';
import { ArrowLeftLine } from '@datanomik/core-ui/icons';

import { isDemo } from '@/config/apiUrls';
import { ClientSelector } from '@/modern/clients';
import { SsoRedirect } from '@/features/login/SsoRedirect';

import { AccountVerificationDialog } from './components/AccountVerificationDialog';
import { AuthLayout } from './components/AuthLayout';
import { DatanomikMark } from './components/DatanomikMark';
import { CredentialsForm } from './components/CredentialsForm';
import { MfaTokenForm } from './components/MfaTokenForm';
import { PasswordResetDialog } from './components/PasswordResetDialog';
import { SsoForm } from './components/SsoForm';
import { useLoginFlow } from './hooks/useLoginFlow';

const DEMO_EMAIL = 'demo@datanomik.com';

const TITLES: Record<string, string> = {
  credentials: 'Log in to your account',
  sso: 'Log in to your account',
  mfa: 'Open your authenticator app',
  client: 'Choose an account',
};

export function LoginPage() {
  const { t } = useTranslation();
  const flow = useLoginFlow();
  const [resetOpen, setResetOpen] = useState(false);

  const canGoBack = flow.step !== 'credentials';
  const title = flow.step === 'mfa' && flow.challenge?.qrCode
    ? 'Set up your authenticator app'
    : TITLES[flow.step] ?? 'Log in to your account';

  if (flow.ssoRedirecting) {
    return <SsoRedirect onCancel={flow.cancelSso} />;
  }

  return (
    <>
      <AuthLayout>
        <div
          key={flow.step}
          className={flow.direction === 'back' ? 'animate-step-back' : 'animate-step-forward'}
        >
          <div className="flex flex-col gap-6">
            {canGoBack && (
              <Button
                variant="link"
                className="gap-1 self-start text-muted-foreground hover:text-foreground"
                onClick={() => flow.goTo('credentials')}
              >
                <ArrowLeftLine aria-hidden="true" />
                {t('Back')}
              </Button>
            )}

            <header className="flex flex-col items-center gap-3">
              <DatanomikMark className="size-8 text-primary" />
              <Text variant="subtitle" as="h1">{t(title)}</Text>
            </header>

            {flow.error && <Alert tone="destructive">{flow.error}</Alert>}

            {flow.step === 'credentials' && (
              <CredentialsForm
                busy={flow.busy}
                defaultEmail={isDemo() ? DEMO_EMAIL : ''}
                onSubmit={flow.submitCredentials}
                onForgotPassword={() => setResetOpen(true)}
                onUseSso={() => flow.goTo('sso')}
              />
            )}

            {flow.step === 'sso' && (
              <SsoForm
                busy={flow.busy}
                defaultDomain={flow.savedLoginDomain}
                onSubmit={flow.submitSsoDomain}
              />
            )}

            {flow.step === 'mfa' && flow.challenge && (
              <MfaTokenForm
                challenge={flow.challenge}
                busy={flow.busy}
                onSubmit={flow.submitMfaToken}
              />
            )}

            {flow.step === 'client' && (
              <ClientSelector
                clients={flow.clients}
                busy={flow.busy}
                onSelect={flow.submitClient}
              />
            )}

          </div>
        </div>
      </AuthLayout>

      <PasswordResetDialog open={resetOpen} onOpenChange={setResetOpen} />

      {flow.unverified && (
        <AccountVerificationDialog
          session={flow.unverified}
          onVerified={() => { window.location.href = '/#/'; }}
          onCancel={flow.clearUnverified}
        />
      )}

      <BlockingOverlay open={flow.busy && flow.step === 'client'} />
    </>
  );
}
